import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';

import Icon from '../../components/AppIcon';
import LocalStorageViewer from './components/LocalStorageViewer';
import DataManagement from './components/DataManagement';
import SystemSettings from './components/SystemSettings';
import WorkOrderImport from './components/WorkOrderImport';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('localStorage');
  const [storageStats, setStorageStats] = useState({
    companies: 0,
    users: 0,
    totalSize: 0
  });

  useEffect(() => {
    calculateStorageStats();
  }, [activeTab]);

  const calculateStorageStats = () => {
    try {
      const companies = JSON.parse(localStorage.getItem('companies') || '[]');
      const users = JSON.parse(localStorage.getItem('users') || '[]');
      
      const totalSize = new Blob([localStorage.getItem('companies') || '', localStorage.getItem('users') || ''])?.size;
      
      setStorageStats({
        companies: companies?.length,
        users: users?.length,
        totalSize: (totalSize / 1024)?.toFixed(2)
      });
    } catch (error) {
      console.error('Error calculating storage stats:', error);
    }
  };

  const tabs = [
    { id: 'localStorage', label: 'LocalStorage Viewer', icon: 'Database' },
    { id: 'dataManagement', label: 'Data Management', icon: 'FolderSync' },
    { id: 'workOrderImport', label: 'Work Order Import', icon: 'FileSpreadsheet' },
    { id: 'systemSettings', label: 'System Settings', icon: 'Settings' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-6 py-8">
        <Breadcrumb
          items={[
            { label: 'Home', path: '/' },
            { label: 'Settings', path: '/settings' }
          ]}
        />

        <div className="flex items-center justify-between mb-8 mt-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Settings</h1>
            <p className="text-muted-foreground mt-2">System configuration and localStorage management</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-card rounded-xl border border-border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="Building2" size={20} className="text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Companies</p>
                <p className="text-2xl font-bold text-foreground">{storageStats?.companies}</p>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-xl border border-border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                <Icon name="Users" size={20} className="text-accent" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Users</p>
                <p className="text-2xl font-bold text-foreground">{storageStats?.users}</p>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-xl border border-border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <Icon name="HardDrive" size={20} className="text-success" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Storage Used</p>
                <p className="text-2xl font-bold text-foreground">{storageStats?.totalSize} KB</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <div className="border-b border-border">
            <div className="flex overflow-x-auto">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-all duration-200 border-b-2 whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-accent/5'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="p-6">
            {activeTab === 'localStorage' && <LocalStorageViewer onDataChange={calculateStorageStats} />}
            {activeTab === 'dataManagement' && <DataManagement onDataChange={calculateStorageStats} />}
            {activeTab === 'workOrderImport' && <WorkOrderImport />}
            {activeTab === 'systemSettings' && <SystemSettings />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;